import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { settingApi } from '../../../slice/settingApi';

import type { listParamsType } from '../../../slice/types';

type addUserParamsType = {
  firstName: string;
  lastName: string;
  password: string;
  email: string;
};

type updateUserParamType = {
  id: string;
  ProfileURL?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  extNumber?: string;
  userExtId?: string;
  UserExtPassword?: string;
  messageSignature?: string;
};

interface IRole {
  id: string;
  name: string;
  description: string;
  parentId?: string | null;
  createdAt?: string;
  status?: string | null;
  tenantId?: string | null;
  updatedAt?: string;
  dataScope?: string;
}

interface IUserDtl {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  roles: IRole[];
  teamId: string | null;
  createdAt: string;
  status: string;
  extNumber: string;
  userExtId: string;
  userExtPassword: string;
  ProfileURL?: string | null;
  roleId?: string;
  reportTo?: string;
  isEmailVerified?: boolean;
  reportToUser?: any;
  phoneNumber?: string;
}

const PRESENCE_CHUNK = 200;

const USER_API = {
  list: 'api/users/list',
  add: 'api/users',
  update: 'api/users',
  details: 'api/users/details',
  delete: 'api/users',
  internalList: 'api/users/internal-list',
  invite: 'api/users',
  inviteResend: 'api/users/invite/resend',
  roles: 'api/users/getRoles',
  resetPassword: 'api/users/password/reset',
  presence: 'api/users/presence',
};

export const userApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // 6. Others
    // ----------------------------------------------------------------------

    // 1. List (Filter)
    getUserList: builder.mutation({
      query: ({ filters = [], globalSearch = '', page = 1, limit = 10 }: listParamsType) => ({
        url: USER_API.list,
        method: 'POST',
        body: {
          filters,
          globalSearch,
          page,
          limit,
        },
      }),
      transformResponse: (response: { data?: { list?: any; total?: number } }) => ({
        list: response?.data?.list ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),

    filterUserDetail: builder.mutation({
      query: (filterQuery) => ({
        url: USER_API.list,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: any; total?: number } }) => ({
        list: response?.data?.list ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),

    getInternalUserList: builder.mutation({
      query: ({ filters = [], globalSearch = '', page = 1, limit = 10 }: listParamsType) => ({
        url: USER_API.internalList,
        method: 'POST',
        body: {
          filters,
          globalSearch,
          page,
          limit,
        },
      }),
      transformResponse: (response: { data?: { user?: { list?: any; total?: number } } }) => ({
        list: response?.data?.user?.list ?? [],
        total: response?.data?.user?.total ?? 0,
      }),
    }),

    // 2. Details
    getUserDtl: builder.mutation({
      query: (id: string) => ({
        url: `${USER_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { user: IUserDtl } }) => {
        const userResp = response.data?.user ?? null;
        const userDtl = {
          id: userResp?.id ?? '',
          firstName: userResp?.firstName ?? '',
          lastName: userResp?.lastName ?? '',
          email: userResp?.email ?? '',
          roles: userResp?.roles ?? [],
          status: userResp?.status ?? 'Active',
          extNumber: userResp?.extNumber ?? '',
          userExtId: userResp?.userExtId ?? '',
          userExtPassword: userResp?.userExtPassword ?? '',
          ProfileURL: userResp?.ProfileURL ?? '',
          reportTo: userResp?.reportToUser ?? '',
          roleId: userResp?.roleId ?? '',
          isEmailVerified: userResp?.isEmailVerified ?? false,
          createdAt: userResp?.createdAt ?? '',
          phoneNumber: userResp?.phoneNumber ?? '',
        };

        return userDtl;
      },
    }),

    // 3. Create
    createUser: builder.mutation({
      query: ({ firstName, lastName, password, email }: addUserParamsType) => ({
        url: USER_API.add,
        method: 'POST',
        body: { firstName, lastName, password, email },
      }),
    }),

    addUser: builder.mutation({
      query: (newUserDetail) => ({
        url: USER_API.invite,
        method: 'POST',
        body: newUserDetail,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // 4. Update
    updateUser: builder.mutation({
      query: ({ id, updates }) => ({
        url: `${USER_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    updateUserDtl: builder.mutation({
      query: ({ id, updates }) => ({
        url: `${USER_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // 5. Delete
    deleteUser: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${USER_API.delete}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // 6. Others
    resendMail: builder.mutation({
      query: (recordId) => ({
        url: USER_API.inviteResend,
        method: 'POST',
        body: recordId,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    getUserRolesList: builder.mutation({
      query: () => ({
        url: USER_API.roles,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { roles?: any } }) => ({
        list: (response?.data?.roles ?? []).filter(
          (role: any) => role?.name?.toLowerCase() !== 'management'
        ),
        // totalPages: response?.data?.roles?.totalPages ?? 0,
      }),
    }),

    convertManagementUser: builder.mutation({
      query: ({ id, roleId }: { id: string; roleId?: string }) => ({
        url: `api/users/${id}/convert-management`,
        method: 'POST',
        body: roleId ? { roleId } : {},
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ user_id, currentPassword, newPassword }) => ({
        url: USER_API.resetPassword, // Use the correct endpoint
        method: 'POST',
        body: {
          user_id,
          password: currentPassword,
          newpassword: newPassword,
        },
      }),
    }),

    /** Bootstrap map of userId → online (socket-connected agent tab). Chunks ids to ≤200 per request. */
    getUsersPresence: builder.query<Record<string, boolean>, string[]>({
      async queryFn(userIds, _api, _extraOptions, fetchWithBQ) {
        const unique = [
          ...new Set(
            userIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
          ),
        ];
        if (!unique.length) {
          return { data: {} };
        }
        const merged: Record<string, boolean> = {};
        for (let i = 0; i < unique.length; i += PRESENCE_CHUNK) {
          const chunk = unique.slice(i, i + PRESENCE_CHUNK);
          const res = await fetchWithBQ({
            url: `${USER_API.presence}?ids=${chunk.map(encodeURIComponent).join(',')}`,
            method: 'GET',
          });
          if (res.error) {
            return { error: res.error as FetchBaseQueryError };
          }
          const body = res.data as { data?: { presence?: Record<string, boolean> } };
          Object.assign(merged, body?.data?.presence ?? {});
        }
        return { data: merged };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        [
          ...new Set(
            queryArgs.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
          ),
        ]
          .sort()
          .join('|'),
    }),
  }),
});

export const {
  useGetUserListMutation,
  useGetInternalUserListMutation,
  useCreateUserMutation,
  useFilterUserDetailMutation,
  useUpdateUserMutation,
  useAddUserMutation,
  useResendMailMutation,
  useDeleteUserMutation,
  useGetUserDtlMutation,
  useUpdateUserDtlMutation,
  useGetUserRolesListMutation,
  useConvertManagementUserMutation,
  useResetPasswordMutation,
  useGetUsersPresenceQuery,
} = userApi;
